import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Mic, MicOff } from "lucide-react";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { useReceiveActionAccess } from "@/hooks/livekit/useReceiveActionAccess";
import { selectCurrentRoom, setMic } from "@/context/features/room/roomSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentAuth } from "@/context/features/auth/authSlice";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Popover, PopoverAnchor, PopoverContent } from "../ui/popover";
import { useTranslation } from "react-i18next";
import { useSidebar } from "../ui/sidebar";

const MIC_REMINDER_INTERVAL_MS = 40 * 1000;

export default function MicToggle({
  togglefunc,
}: {
  togglefunc: (() => Promise<void>) | undefined;
}) {
  const { livekitContext } = useLivekitContext();
  const { room } = livekitContext ?? {};
  const { user } = useSelector(selectCurrentAuth);
  const { isMicOpen } = useSelector(selectCurrentRoom);
  const [isMicLocked, setLocked] = useState<boolean>(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const { openMobile } = useSidebar();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (isMicOpen || isMicLocked || openMobile) {
      setReminderOpen(false);
      return;
    }
    setReminderOpen(true);
    const id = setInterval(
      () => setReminderOpen(true),
      MIC_REMINDER_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [isMicOpen, isMicLocked, openMobile]);

  useReceiveActionAccess("mic", room, (data) => {
    if (user?.id.toString() === data.to) {
      if (data.type === "lock" && isMicOpen) {
        if (typeof togglefunc === "function") togglefunc();
        dispatch(setMic(false));
      }
      setLocked(data.type === "lock");
    }
  });

  const micToggleHandler = () => {
    if (typeof togglefunc === "function") {
      togglefunc();
      dispatch(setMic(!isMicOpen));
    }
  };
  return (
    <Popover open={reminderOpen} onOpenChange={setReminderOpen}>
      <PopoverAnchor asChild>
        <span className="inline-block">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={micToggleHandler}
                size="icon"
                disabled={isMicLocked}
              >
                {isMicOpen ? (
                  <Mic strokeWidth={1.5} />
                ) : (
                  <MicOff strokeWidth={1.5} />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {isMicLocked
                ? t("locked")
                : isMicOpen
                  ? t("close_mic")
                  : t("open_mic")}
            </TooltipContent>
          </Tooltip>
        </span>
      </PopoverAnchor>
      <PopoverContent side="top" className="w-auto max-w-[18rem]">
        <h4 className="flex items-center gap-1 text-amber-600 font-semibold text-lg">
          <Mic strokeWidth={2} className="mb-1 size-5" />
          {t("alert")}
        </h4>
        <p className="text-sm text-foreground">{t("open_mic_reminder")}</p>
      </PopoverContent>
    </Popover>
  );
}
